// Etiqueta impressa pela balança (Filizola, Toledo...): EAN-13 começando com
// "2", com o código do produto e o valor (ou peso) embutidos. Layout
// configurável em Parâmetros → PDV → Etiqueta de balança, mesmo modelo do
// Link Pro. Exemplo real (goldenpet, 2026-10-26), padrão código 5 dígitos a
// partir do 2º + valor com 2 casas:
//   2 30869 0 00464 2  →  produto 30869, R$ 4,64
//                          (0,265 kg x R$ 17,50 — o peso não vem no código)

export const ETIQUETA_PADRAO = {
  ativo: true,
  usar: 'codigo',     // 'codigo' = código interno | 'barras' = código de barras do produto
  informar: 'valor',  // 'valor' = total R$ | 'peso' = kg
  decimais: 2,
  inicio: 2,          // posição (1-based) do 1º dígito do código do produto
  tamanho: 5,         // quantos dígitos tem o código do produto
};

export function configEtiqueta(pdvConfig) {
  const c = { ...ETIQUETA_PADRAO, ...(pdvConfig?.balanca_etiqueta || {}) };
  const int = (v, min, max, padrao) => {
    const n = parseInt(v, 10);
    return Number.isFinite(n) && n >= min && n <= max ? n : padrao;
  };
  c.inicio   = int(c.inicio, 2, 7, ETIQUETA_PADRAO.inicio);
  c.tamanho  = int(c.tamanho, 4, 6, ETIQUETA_PADRAO.tamanho);
  c.decimais = int(c.decimais, 0, 3, ETIQUETA_PADRAO.decimais);
  return c;
}

export function digitoEan13Valido(codigo) {
  if (!/^\d{13}$/.test(codigo)) return false;
  const soma = [...codigo.slice(0, 12)].reduce((s, d, i) => s + Number(d) * (i % 2 ? 3 : 1), 0);
  return (10 - (soma % 10)) % 10 === Number(codigo[12]);
}

// null = não é etiqueta de balança (segue a busca normal pelo código).
// O campo de valor/peso é tudo entre o fim do código e o dígito verificador.
export function lerEtiquetaBalanca(termo, cfg) {
  const codigo = String(termo || '').trim();
  if (!cfg?.ativo || !/^2\d{12}$/.test(codigo) || !digitoEan13Valido(codigo)) return null;
  const ini = cfg.inicio - 1;
  const fim = ini + cfg.tamanho;
  const campo = codigo.slice(fim, 12);
  if (!campo) return null;
  const numero = Number(campo) / 10 ** cfg.decimais;
  return {
    codigoProduto: codigo.slice(ini, fim),
    valor: cfg.informar === 'peso' ? null : numero,
    peso:  cfg.informar === 'peso' ? numero : null,
  };
}

// Etiqueta por valor: a quantidade sai do valor ÷ preço/kg (3 casas, igual
// ao peso que a balança imprime). Com o mesmo preço nos dois lados, o total
// do item fecha com o total da etiqueta.
export function quantidadeDaEtiqueta(etiqueta, preco) {
  if (etiqueta.peso != null) return etiqueta.peso;
  if (!(preco > 0)) return null;
  return Math.round((etiqueta.valor / preco) * 1000) / 1000;
}

// Caminho inverso de lerEtiquetaBalanca, pra tela de Pesagem imprimir a
// etiqueta no MESMO layout que o PDV lê: "2" + zeros até o início do código
// + código do produto + valor (ou peso) até o 12º dígito + verificador.
export function digitoEan13(doze) {
  const soma = [...doze].reduce((s, d, i) => s + Number(d) * (i % 2 ? 3 : 1), 0);
  return String((10 - (soma % 10)) % 10);
}

export function montarEtiquetaBalanca({ codigoProduto, valor, peso }, cfg) {
  const ini = cfg.inicio - 1;
  const fim = ini + cfg.tamanho;
  const cod = String(codigoProduto ?? '').trim();
  if (!/^\d+$/.test(cod) || cod.length > cfg.tamanho) {
    throw new Error(`O código "${cod}" não cabe na etiqueta (${cfg.tamanho} dígitos numéricos — Parâmetros → PDV → Etiqueta de balança).`);
  }
  const numero = cfg.informar === 'peso' ? peso : valor;
  const digitos = 12 - fim;
  const campo = String(Math.round(Number(numero) * 10 ** cfg.decimais));
  if (!(Number(numero) > 0) || campo.length > digitos) {
    throw new Error(`${cfg.informar === 'peso' ? 'Peso' : 'Valor'} fora do que a etiqueta comporta (${digitos} dígitos com ${cfg.decimais} casas).`);
  }
  const doze = '2' + '0'.repeat(ini - 1) + cod.padStart(cfg.tamanho, '0') + campo.padStart(digitos, '0');
  return doze + digitoEan13(doze);
}

// Código do produto na etiqueta vem com zeros à esquerda ("00123").
export function produtoBateEtiqueta(produto, etiqueta, cfg) {
  if (!produto?.produto_balanca) return false;
  const alvo = etiqueta.codigoProduto;
  const igual = v => v != null && v !== '' && /^\d+$/.test(String(v)) && Number(v) === Number(alvo);
  if (cfg.usar === 'barras') {
    return igual(produto.codigo_barras) || (produto.codigos_barras || []).some(cb => igual(cb.codigo));
  }
  return igual(produto.codigo_interno);
}
